// @ts-nocheck
import React, {
  FunctionComponent,
  useEffect,
  useState,
  useContext,
} from "react";
import { RouteComponentProps } from "react-router";
import { sortBy } from "sort-by-typescript";
import { AppPage, LoadingMask, PlanCard } from "../../components";
import {
  SearchResultPlan,
  PlanType,
  ActionType,
  PlanSNPType,
  SearchResultsFilters,
  SortOptions,
} from "../../@types";
import { getPlans } from "../../api";
import { isAvailable } from "../../helpers";
import PlanFiltersContainer from "../SearchResultsPage/PlanFiltersContainer";
import { AppStore, defaultState } from "../../app/store";
import SortSelector from "../SearchResultsPage/SortSelector";
import { Pager } from "../../components/Pager";
import SpecialNeedsPlansFilters from "../SearchResultsPage/SpecialNeedsPlansFilters";
import { Button } from "@cmsgov/ds-medicare-gov";

const LIMIT = 10;

const SearchResults: FunctionComponent<RouteComponentProps> = props => {
  const {
    state: {
      pharmacies,
      prescriptions,
      lis,
      fips,
      zipcode,
      snpFilters = [PlanSNPType.SNP_TYPE_NOT_SNP],
      searchResultsFilters = defaultState.searchResultsFilters,
    },
    dispatch,
  } = useContext(AppStore.AppContext);
  const [maPlans, setMaPlans] = useState([] as SearchResultPlan[]);
  const [pdpPlans, setPdpPlans] = useState([] as SearchResultPlan[]);
  const [plansToShow, setPlansToShow] = useState([] as SearchResultPlan[]);
  const [planType, setPlanType] = useState(PlanType.MAPD);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [timer, setTimer] = useState(Date.now());
  const [timeItTook, setTimeItTook] = useState(0);

  const filterPlans = (
    plans: SearchResultPlan[],
    theFilters: SearchResultsFilters,
    theSnpFilters: PlanSNPType[]
  ): SearchResultPlan[] => {
    setPage(0);
    const filteredPlans = plans.filter(plan => {
      const benefits = Object.keys(theFilters.filters).map(filter => {
        switch (filter) {
          case "silver_sneakers":
            return plan.silver_sneakers;
          case "transportation":
            return plan.transportation;
          case "hearing_coverage":
            return plan.package_services
              ? isAvailable(plan.package_services.hearing_services)
              : false;
          case "vision_coverage":
            return plan.package_services
              ? isAvailable(plan.package_services.vision_services)
              : false;
          case "dental_coverage":
            return plan.package_services
              ? isAvailable(plan.package_services.dental_services)
              : false;
          default:
            return false;
        }
      });

      const starRatingMatch = theFilters.starRatingFilter
        ? plan.overall_star_rating.rating >= Number(theFilters.starRatingFilter)
        : true;

      const insuranceCarrierMatch = theFilters.insuranceCarrier
        ? plan.organization_name === theFilters.insuranceCarrier
        : true;

      const snpTypeMatch = theSnpFilters.length
        ? theSnpFilters.includes(plan.snp_type)
        : plan.snp_type === PlanSNPType.SNP_TYPE_NOT_SNP;

      return (
        !benefits.some(benefit => benefit === false) &&
        starRatingMatch &&
        insuranceCarrierMatch &&
        snpTypeMatch
      );
    });
    return filteredPlans;
  };

  const sortPlans = (
    plans: SearchResultPlan[],
    sortOn = SortOptions.ANNUAL_TOTAL
  ): SearchResultPlan[] => {
    setPage(0);
    switch (sortOn) {
      case SortOptions.ANNUAL_TOTAL:
        return [...plans].sort(
          sortBy(
            "remaining_premium_and_drugs",
            "-overall_star_rating.rating",
            "annual_deductible",
            "drug_plan_deductible",
            "maximum_oopc"
          )
        );
      case SortOptions.MONTHLY_PREMIUM:
        return [...plans].sort(
          sortBy(
            "calculated_monthly_premium",
            "-overall_star_rating.rating",
            "annual_deductible",
            "drug_plan_deductible",
            "maximum_oopc"
          )
        );
      case SortOptions.ANNUAL_DEDUCTIBLE:
        return [...plans].sort(
          sortBy(
            "annual_deductible",
            "-overall_star_rating.rating",
            "drug_plan_deductible",
            "maximum_oopc"
          )
        );
      case SortOptions.DRUG_PLAN_DEDUCTIBLE:
        return [...plans].sort(
          sortBy(
            "drug_plan_deductible",
            "-overall_star_rating.rating",
            "annual_deductible",
            "maximum_oopc"
          )
        );
    }
    return plans;
  };

  const requestPlans = async (): Promise<void> => {
    if (!fips) return;

    const paramsToSend = {
      fips,
      page: 0,
      plan_type: [PlanType.MA, PlanType.MAPD, PlanType.PDP],
      snp_type: [
        PlanSNPType.SNP_TYPE_NOT_SNP,
        PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE,
        PlanSNPType.SNP_TYPE_CHRONIC_OR_DISABLING,
        PlanSNPType.SNP_TYPE_INSTITUTIONAL,
      ],
      sort_order: SortOptions.ANNUAL_TOTAL,
      year: "2020",
      zip: zipcode,
      count: 999,
    };
    const body = {
      npis: pharmacies.map(pharmacy => pharmacy.npi),
      prescriptions: prescriptions.map(prescription => {
        return {
          ndc: prescription.ndc,
          quantity: prescription.quantity,
          frequency: prescription.frequency,
        };
      }),
      lis,
    };
    try {
      const result = await getPlans(paramsToSend, body);
      setTimer(Date.now());
      setLoading(false);
      const maPlans = result.plans.filter(plan => {
        return [PlanType.MA, PlanType.MAPD].includes(plan.plan_type);
      });
      const pdpPlans = result.plans.filter(plan => {
        return plan.plan_type === PlanType.PDP;
      });
      setMaPlans(maPlans);
      setPdpPlans(pdpPlans);
      const thePlans = planType === PlanType.PDP ? pdpPlans : maPlans;
      setPlansToShow(
        sortPlans(
          filterPlans(thePlans, searchResultsFilters, snpFilters),
          searchResultsFilters.sortBy
        )
      );
      dispatch({
        type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS,
        payload: result.search_summary.organization_names,
      });
    } catch (e) {
      setLoading(false);
      setMaPlans([]);
      setPdpPlans([]);
      setPlansToShow([]);
    }
  };

  const willNotNeed = (): void => {
    // we won't need this function and can remove it from the components
  };

  const clearFilters = (): void => {
    const plans = planType === PlanType.PDP ? pdpPlans : maPlans;
    setPlansToShow(plans);
  };

  useEffect(() => {
    setLoading(true);
    requestPlans();
  }, [zipcode]);

  useEffect(() => {
    const thePlans = planType === PlanType.PDP ? pdpPlans : maPlans;
    if (thePlans.length) {
      setPage(0);
      setPlansToShow(thePlans);
    }
  }, [planType]);

  useEffect(() => {
    const thePlans = planType === PlanType.PDP ? pdpPlans : maPlans;
    if (thePlans.length) {
      setPlansToShow(filterPlans(thePlans, searchResultsFilters, snpFilters));
    }
  }, [
    searchResultsFilters.filters,
    searchResultsFilters.starRatingFilter,
    searchResultsFilters.insuranceCarrier,
    snpFilters,
  ]);

  useEffect(() => {
    if (plansToShow.length) {
      const sortedPlans = sortPlans(plansToShow, searchResultsFilters.sortBy);
      setPlansToShow(sortedPlans);
    }
  }, [searchResultsFilters.sortBy]);

  useEffect(() => {
    if (plansToShow.length) {
      setTimeItTook(Date.now() - timer);
    }
  }, [plansToShow]);

  const pageOfPlans = plansToShow.slice(LIMIT * page, LIMIT * page + LIMIT);
  const otherPlanType =
    planType === PlanType.PDP ? PlanType.MAPD : PlanType.PDP;

  return (
    <AppPage>
      <LoadingMask loading={loading} delay={500} />
      <h1>Search Results</h1>
      <span className="ds-u-float--right">{timeItTook}</span>
      <div>
        {planType === PlanType.PDP ? pdpPlans.length : maPlans.length}{" "}
        {planType}
        <br />
        <Button
          onClick={(): void => {
            setPlanType(otherPlanType);
          }}
        >
          {planType === PlanType.PDP ? maPlans.length : pdpPlans.length}{" "}
          {otherPlanType}
        </Button>
      </div>
      <small>MA total includes SNP plans</small>
      <PlanFiltersContainer
        resetToFirstPageOfResults={clearFilters}
        loadingPlans={loading}
        numPlans={plansToShow.length}
      />
      <SortSelector
        onSortChanged={willNotNeed}
        defaultSort={SortOptions.ANNUAL_TOTAL}
      />
      {planType !== PlanType.PDP && (
        <SpecialNeedsPlansFilters onSpecialNeedsPlansUpdated={willNotNeed} />
      )}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          Showing {LIMIT * page + 1} to {LIMIT * page + pageOfPlans.length} of{" "}
          {plansToShow.length} plans
          <ul>
            {pageOfPlans.map(plan => {
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  handleAddToCompare={(): void => undefined}
                  {...props}
                />
              );
            })}
          </ul>
        </>
      )}
      <Pager
        currentPage={page}
        pageCount={Math.ceil(plansToShow.length / LIMIT)}
        handlePageClick={(pageNum: number): void => {
          setPage(pageNum);
        }}
        onNextPage={(): void => {
          const nextPage = page + 1;

          if (nextPage < Math.ceil(plansToShow.length / LIMIT)) {
            setPage(nextPage);
          }
        }}
        onPrevPage={(): void => {
          if (page > 1) {
            setPage(page - 1);
          }
        }}
      />
    </AppPage>
  );
};

export default SearchResults;
